import React, { useCallback, useEffect, useState } from "react";
import { MdBarChart, MdDashboard } from "react-icons/md";
import { useDashboardStats } from "../useHook";
import DashboardAnalyticsPanel from "../components/DashboardAnalyticsPanel";

const PERIOD_OPTIONS = [
  { value: "all", label: "All" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_year", label: "This Year" },
  { value: "last_year", label: "Last Year" },
];

const formatNumber = (value) => {
  if (value === null || value === undefined) return "--";
  const n = Number(value);
  if (!Number.isFinite(n)) return "--";
  return n.toLocaleString();
};

const formatGrowth = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n}%`;
};

const hexToRgba = (hex, alpha = 0.12) => {
  const raw = String(hex || "#982598").replace("#", "");
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  const num = parseInt(full, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const periodLabel = (value) =>
  PERIOD_OPTIONS.find((o) => o.value === value)?.label || "All";

const FilterableCard = ({
  title,
  accent = "#982598",
  period,
  onPeriodChange,
  extraFilters = [],
  value,
  subtitle,
  growth,
  isLoading,
  hideFilters = false,
  onViewAnalytics,
}) => {
  const growthLabel = formatGrowth(growth);
  const growthPositive =
    growth !== null && growth !== undefined && Number(growth) >= 0;
  const filterCols =
    extraFilters.length >= 2
      ? "grid-cols-1"
      : extraFilters.length === 1
        ? "grid-cols-2"
        : "grid-cols-1";

  return (
    <article
      className="group relative flex min-h-[280px] flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(15,23,42,0.08)]"
      style={{
        background: `linear-gradient(165deg, ${hexToRgba(accent, 0.07)} 0%, #ffffff 42%, #ffffff 100%)`,
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: `linear-gradient(90deg, ${accent}, ${hexToRgba(accent, 0.35)})` }}
      />

      <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm font-bold text-white shadow-sm"
            style={{
              background: `linear-gradient(145deg, ${accent}, ${hexToRgba(accent, 0.75)})`,
              boxShadow: `0 8px 16px ${hexToRgba(accent, 0.28)}`,
            }}
          >
            {String(title || "?")
              .split(" ")
              .map((w) => w[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </span>
          <div className="min-w-0">
            <h3 className="truncate text-[15px] font-semibold tracking-tight text-slate-800">
              {title}
            </h3>
            {!hideFilters && (
              <p className="mt-0.5 text-xs text-slate-400">
                Showing ·{" "}
                <span className="font-medium text-slate-500">
                  {periodLabel(period)}
                </span>
              </p>
            )}
          </div>
        </div>

        {growthLabel && period !== "all" && !isLoading && !hideFilters && (
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
              growthPositive
                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
                : "bg-rose-50 text-rose-600 ring-1 ring-rose-100"
            }`}
          >
            {growthLabel}
          </span>
        )}
      </div>

      <div className={`flex-1 px-6 ${hideFilters ? "pb-6" : "pb-5"}`}>
        <div
          className="text-[2.75rem] leading-none font-bold tracking-tight tabular-nums"
          style={{ color: accent }}
        >
          {isLoading ? (
            <div className="flex h-12 items-center">
              <div
                className="h-8 w-8 animate-spin rounded-full border-[3px] border-slate-200"
                style={{ borderTopColor: accent }}
              />
            </div>
          ) : (
            formatNumber(value)
          )}
        </div>
        {!isLoading && subtitle && (
          <p className="mt-3 text-sm leading-relaxed text-slate-500">{subtitle}</p>
        )}
      </div>

      {!hideFilters && (
        <div className="mt-auto border-t border-slate-100/90 bg-slate-50/70 px-6 py-4 backdrop-blur-sm">
          <div className={`grid gap-3 ${filterCols}`}>
            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                Period
              </label>
              <select
                className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 shadow-sm outline-none transition focus:border-[#982598] focus:ring-2 focus:ring-[#982598]/15"
                value={period}
                onChange={(e) => onPeriodChange(e.target.value)}
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {extraFilters.map((field) => (
              <div key={field.key}>
                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                  {field.label}
                </label>
                <select
                  className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 shadow-sm outline-none transition focus:border-[#982598] focus:ring-2 focus:ring-[#982598]/15"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {field.options.map((opt) => (
                    <option key={String(opt.value)} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {onViewAnalytics && (
            <button
              type="button"
              onClick={onViewAnalytics}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#982598]/30 hover:bg-[#982598]/5 hover:text-[#982598]"
            >
              <MdBarChart size={18} />
              View Charts
            </button>
          )}
        </div>
      )}
    </article>
  );
};

const pickPeriodValue = (data, period) => {
  if (!data) return null;
  if (period === "all") {
    return data.summary?.total ?? data.summary?.users ?? null;
  }
  return (
    data.comparison?.current?.total ??
    data.summary?.total ??
    null
  );
};

function Dashboard() {
  const {
    getStats,
    getDesignsAnalytics,
    getInteriorDesignsAnalytics,
    getShopAnalytics,
    getBlogsAnalytics,
    getProjectsAnalytics,
    getContractsAnalytics,
    getPricingAnalytics,
  } = useDashboardStats();

  const [activeTab, setActiveTab] = useState("overview");
  const [analyticsModule, setAnalyticsModule] = useState("users");

  const openAnalytics = useCallback((moduleKey) => {
    setAnalyticsModule(moduleKey);
    setActiveTab("analytics");
  }, []);

  const [cards, setCards] = useState({
    users: { period: "all", loading: true, value: null, subtitle: "", growth: null },
    orders: { period: "all", status: "", loading: true, value: null, subtitle: "", growth: null },
    products: { period: "all", status: "", loading: true, value: null, subtitle: "", growth: null },
    designs: { period: "all", loading: true, value: null, subtitle: "", growth: null },
    interiors: { period: "all", loading: true, value: null, subtitle: "", growth: null },
    blogs: { period: "all", status: "", loading: true, value: null, subtitle: "", growth: null },
    projects: { period: "all", status: "", loading: true, value: null, subtitle: "", growth: null },
    contracts: { period: "all", status: "", type: "all", loading: true, value: null, subtitle: "", growth: null },
    pricing: { period: "all", type: "all", loading: true, value: null, subtitle: "", growth: null },
  });

  const updateCard = useCallback((key, patch) => {
    setCards((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const loadUsers = useCallback(
    async (period) => {
      updateCard("users", { loading: true, period });
      try {
        const data = await getStats({ period, groupBy: "monthly" });
        const value =
          period === "all"
            ? data?.summary?.users
            : data?.comparison?.users?.current?.total;
        updateCard("users", {
          loading: false,
          value,
          subtitle:
            period === "all"
              ? "Registered customers"
              : `New customers · ${data?.period?.label || periodLabel(period)}`,
          growth: data?.comparison?.users?.growthPercentage,
        });
      } catch {
        updateCard("users", { loading: false });
      }
    },
    [getStats, updateCard],
  );

  const loadOrders = useCallback(
    async (period, status = "") => {
      updateCard("orders", { loading: true, period, status });
      try {
        const params = { period, groupBy: "monthly" };
        if (status) params.orderStatus = status;
        const data = await getShopAnalytics(params);
        const orders = data?.orders || {};
        const chartTotal = data?.ordersChart?.totals;
        updateCard("orders", {
          loading: false,
          value: period === "all" ? orders.total : chartTotal ?? orders.total,
          subtitle: `${formatNumber(orders.approvedCount)} approved · Gross Merchandise Value ${formatNumber(orders.gmv)}`,
          growth: data?.comparison?.growthPercentage,
        });
      } catch {
        updateCard("orders", { loading: false });
      }
    },
    [getShopAnalytics, updateCard],
  );

  const loadProducts = useCallback(
    async (period, status = "") => {
      updateCard("products", { loading: true, period, status });
      try {
        const params = { period, groupBy: "monthly" };
        if (status !== "") params.status = status;
        const data = await getShopAnalytics(params);
        const s = data?.summary || {};
        updateCard("products", {
          loading: false,
          value: period === "all" ? s.total : data?.comparison?.current?.total,
          subtitle: `${formatNumber(s.active)} active · ${formatNumber(s.inactive)} inactive`,
          growth: data?.comparison?.growthPercentage,
        });
      } catch {
        updateCard("products", { loading: false });
      }
    },
    [getShopAnalytics, updateCard],
  );

  const loadDesigns = useCallback(
    async (period) => {
      updateCard("designs", { loading: true, period });
      try {
        const data = await getDesignsAnalytics({ period, groupBy: "monthly" });
        const s = data?.summary || {};
        updateCard("designs", {
          loading: false,
          value: pickPeriodValue(data, period),
          subtitle: `${formatNumber(s.homeScreen)} home · ${formatNumber(s.favourites)} favourites`,
          growth: data?.comparison?.growthPercentage,
        });
      } catch {
        updateCard("designs", { loading: false });
      }
    },
    [getDesignsAnalytics, updateCard],
  );

  const loadInteriors = useCallback(
    async (period) => {
      updateCard("interiors", { loading: true, period });
      try {
        const data = await getInteriorDesignsAnalytics({
          period,
          groupBy: "monthly",
        });
        const s = data?.summary || {};
        updateCard("interiors", {
          loading: false,
          value: pickPeriodValue(data, period),
          subtitle: `${formatNumber(s.favourites)} favourites`,
          growth: data?.comparison?.growthPercentage,
        });
      } catch {
        updateCard("interiors", { loading: false });
      }
    },
    [getInteriorDesignsAnalytics, updateCard],
  );

  const loadBlogs = useCallback(
    async (period, status = "") => {
      updateCard("blogs", { loading: true, period, status });
      try {
        const params = { period, groupBy: "monthly" };
        if (status) params.status = status;
        const data = await getBlogsAnalytics(params);
        const s = data?.summary || {};
        updateCard("blogs", {
          loading: false,
          value: pickPeriodValue(data, period),
          subtitle: `${formatNumber(s.published)} published · ${formatNumber(s.draft)} drafts`,
          growth: data?.comparison?.growthPercentage,
        });
      } catch {
        updateCard("blogs", { loading: false });
      }
    },
    [getBlogsAnalytics, updateCard],
  );

  const loadProjects = useCallback(
    async (period, status = "") => {
      updateCard("projects", { loading: true, period, status });
      try {
        const params = { period, groupBy: "monthly" };
        if (status !== "") params.status = status;
        const data = await getProjectsAnalytics(params);
        const s = data?.summary || {};
        updateCard("projects", {
          loading: false,
          value: pickPeriodValue(data, period),
          subtitle: `${formatNumber(s.active)} active · ${formatNumber(s.inactive)} inactive`,
          growth: data?.comparison?.growthPercentage,
        });
      } catch {
        updateCard("projects", { loading: false });
      }
    },
    [getProjectsAnalytics, updateCard],
  );

  const loadContracts = useCallback(
    async (period, status = "", type = "all") => {
      updateCard("contracts", { loading: true, period, status, type });
      try {
        const params = { period, groupBy: "monthly", type };
        if (status) params.status = status;
        const data = await getContractsAnalytics(params);
        const c = data?.summary?.construction || {};
        const d = data?.summary?.design || {};
        const total =
          period === "all"
            ? (c.total || 0) + (d.total || 0)
            : (data?.comparison?.construction?.current?.total || 0) +
              (data?.comparison?.design?.current?.total || 0);
        updateCard("contracts", {
          loading: false,
          value: total,
          subtitle: `${formatNumber(c.active)} construction · ${formatNumber(d.active)} design active`,
          growth: null,
        });
      } catch {
        updateCard("contracts", { loading: false });
      }
    },
    [getContractsAnalytics, updateCard],
  );

  const loadPricing = useCallback(
    async (period, type = "all") => {
      updateCard("pricing", { loading: true, period, type });
      try {
        const data = await getPricingAnalytics({
          period,
          groupBy: "monthly",
          type,
        });
        const b = data?.summary?.budget || {};
        const p = data?.summary?.premium || {};
        const t = data?.summary?.tentative || {};
        const total =
          period === "all"
            ? (b.total || 0) + (p.total || 0) + (t.total || 0)
            : (data?.comparison?.budget?.current?.total || 0) +
              (data?.comparison?.premium?.current?.total || 0);
        updateCard("pricing", {
          loading: false,
          value: total,
          subtitle: `${formatNumber(b.total)} budget · ${formatNumber(p.total)} premium · ${formatNumber(t.total)} tentative`,
          growth: null,
        });
      } catch {
        updateCard("pricing", { loading: false });
      }
    },
    [getPricingAnalytics, updateCard],
  );

  useEffect(() => {
    loadUsers("all");
    loadOrders("all", "");
    loadProducts("all", "");
    loadDesigns("all");
    loadInteriors("all");
    loadBlogs("all", "");
    loadProjects("all", "");
    loadContracts("all", "", "all");
    loadPricing("all", "all");
    // initial load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pb-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#982598]">
            Analytics Overview
          </p>
          <h2 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">
            Dashboard
          </h2>
        </div>

        <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setActiveTab("overview")}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
              activeTab === "overview"
                ? "bg-[#982598] text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <MdDashboard size={18} />
            Overview
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("analytics")}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
              activeTab === "analytics"
                ? "bg-[#982598] text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <MdBarChart size={18} />
            Analytics
          </button>
        </div>
      </div>

      {activeTab === "analytics" ? (
        <DashboardAnalyticsPanel
          moduleKey={analyticsModule}
          onModuleChange={setAnalyticsModule}
          cardFilters={cards}
        />
      ) : (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        <FilterableCard
          title="Total Users"
          accent="#2563EB"
          period={cards.users.period}
          onPeriodChange={(p) => loadUsers(p)}
          onViewAnalytics={() => openAnalytics("users")}
          value={cards.users.value}
          subtitle={cards.users.subtitle}
          growth={cards.users.growth}
          isLoading={cards.users.loading}
        />

        <FilterableCard
          title="Total Orders"
          accent="#059669"
          period={cards.orders.period}
          onPeriodChange={(p) => loadOrders(p, cards.orders.status)}
          onViewAnalytics={() => openAnalytics("orders")}
          extraFilters={[
            {
              key: "status",
              label: "Order Status",
              value: cards.orders.status,
              onChange: (v) => loadOrders(cards.orders.period, v),
              options: [
                { value: "", label: "All" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
              ],
            },
          ]}
          value={cards.orders.value}
          subtitle={cards.orders.subtitle}
          growth={cards.orders.growth}
          isLoading={cards.orders.loading}
        />

        <FilterableCard
          title="Products"
          accent="#982598"
          period={cards.products.period}
          onPeriodChange={(p) => loadProducts(p, cards.products.status)}
          onViewAnalytics={() => openAnalytics("products")}
          extraFilters={[
            {
              key: "status",
              label: "Status",
              value: cards.products.status,
              onChange: (v) => loadProducts(cards.products.period, v),
              options: [
                { value: "", label: "All" },
                { value: "true", label: "Active" },
                { value: "false", label: "Inactive" },
              ],
            },
          ]}
          value={cards.products.value}
          subtitle={cards.products.subtitle}
          growth={cards.products.growth}
          isLoading={cards.products.loading}
        />

        <FilterableCard
          title="House Designs"
          accent="#4F46E5"
          period={cards.designs.period}
          onPeriodChange={(p) => loadDesigns(p)}
          onViewAnalytics={() => openAnalytics("designs")}
          value={cards.designs.value}
          subtitle={cards.designs.subtitle}
          growth={cards.designs.growth}
          isLoading={cards.designs.loading}
        />

        <FilterableCard
          title="Interior Designs"
          accent="#7C3AED"
          period={cards.interiors.period}
          onPeriodChange={(p) => loadInteriors(p)}
          onViewAnalytics={() => openAnalytics("interiors")}
          value={cards.interiors.value}
          subtitle={cards.interiors.subtitle}
          growth={cards.interiors.growth}
          isLoading={cards.interiors.loading}
        />

        <FilterableCard
          title="Blogs"
          accent="#0284C7"
          period={cards.blogs.period}
          onPeriodChange={(p) => loadBlogs(p, cards.blogs.status)}
          onViewAnalytics={() => openAnalytics("blogs")}
          extraFilters={[
            {
              key: "status",
              label: "Publish Status",
              value: cards.blogs.status,
              onChange: (v) => loadBlogs(cards.blogs.period, v),
              options: [
                { value: "", label: "All" },
                { value: "published", label: "Published" },
                { value: "draft", label: "Draft" },
              ],
            },
          ]}
          value={cards.blogs.value}
          subtitle={cards.blogs.subtitle}
          growth={cards.blogs.growth}
          isLoading={cards.blogs.loading}
        />

        <FilterableCard
          title="Our Projects"
          accent="#D97706"
          period={cards.projects.period}
          onPeriodChange={(p) => loadProjects(p, cards.projects.status)}
          onViewAnalytics={() => openAnalytics("projects")}
          extraFilters={[
            {
              key: "status",
              label: "Status",
              value: cards.projects.status,
              onChange: (v) => loadProjects(cards.projects.period, v),
              options: [
                { value: "", label: "All" },
                { value: "true", label: "Active" },
                { value: "false", label: "Inactive" },
              ],
            },
          ]}
          value={cards.projects.value}
          subtitle={cards.projects.subtitle}
          growth={cards.projects.growth}
          isLoading={cards.projects.loading}
        />

        <FilterableCard
          title="Contracts"
          accent="#E11D48"
          period={cards.contracts.period}
          onPeriodChange={(p) =>
            loadContracts(p, cards.contracts.status, cards.contracts.type)
          }
          onViewAnalytics={() => openAnalytics("contracts")}
          extraFilters={[
            {
              key: "type",
              label: "Type",
              value: cards.contracts.type,
              onChange: (v) =>
                loadContracts(cards.contracts.period, cards.contracts.status, v),
              options: [
                { value: "all", label: "All" },
                { value: "construction", label: "Construction" },
                { value: "design", label: "Design" },
              ],
            },
            {
              key: "status",
              label: "Status",
              value: cards.contracts.status,
              onChange: (v) =>
                loadContracts(cards.contracts.period, v, cards.contracts.type),
              options: [
                { value: "", label: "All" },
                { value: "ACTIVE", label: "Active" },
                { value: "INACTIVE", label: "Inactive" },
              ],
            },
          ]}
          value={cards.contracts.value}
          subtitle={cards.contracts.subtitle}
          growth={cards.contracts.growth}
          isLoading={cards.contracts.loading}
        />

        <FilterableCard
          title="Pricing"
          accent="#EA580C"
          period={cards.pricing.period}
          onPeriodChange={(p) => loadPricing(p, cards.pricing.type)}
          onViewAnalytics={() => openAnalytics("pricing")}
          extraFilters={[
            {
              key: "type",
              label: "Type",
              value: cards.pricing.type,
              onChange: (v) => loadPricing(cards.pricing.period, v),
              options: [
                { value: "all", label: "All" },
                { value: "budget", label: "Budget" },
                { value: "premium", label: "Premium" },
                { value: "tentative", label: "Tentative" },
              ],
            },
          ]}
          value={cards.pricing.value}
          subtitle={cards.pricing.subtitle}
          growth={cards.pricing.growth}
          isLoading={cards.pricing.loading}
        />
      </div>
      )}
    </div>
  );
}

export default Dashboard;
